import React, { useState, useEffect } from "react";
import { FaTrophy } from "react-icons/fa";
import {
  requestGetStudents,
  requestGetEvaluationHistory,
} from "../utils/resource";

const calculateScoreForAngle = (firstAngle, lastAngle) => {
  const evolutionPoints = Math.max(0, firstAngle - lastAngle);
  const performancePoints = Math.max(0, 180 - lastAngle);
  return evolutionPoints + performancePoints;
};

const calculateRankingScore = (history, exerciseName) => {
  const relevantHistory = history
    .filter((entry) => entry.exercise === exerciseName)
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  if (relevantHistory.length < 2) {
    const lastLevel =
      relevantHistory.length > 0
        ? relevantHistory[relevantHistory.length - 1].level
        : null;
    return { score: 0, level: lastLevel };
  }

  const firstEval = relevantHistory[0];
  const lastEval = relevantHistory[relevantHistory.length - 1];

  let totalScore = 0;

  if (exerciseName === "Alongamento do Quadril") {
    const scoreLeft = calculateScoreForAngle(
      firstEval.angles[0],
      lastEval.angles[0]
    );

    const scoreRight = calculateScoreForAngle(
      firstEval.angles[1],
      lastEval.angles[1]
    );

    totalScore = scoreLeft + scoreRight;
  } else {
    totalScore = calculateScoreForAngle(
      firstEval.angles[0],
      lastEval.angles[0]
    );
  }

  return { score: Math.round(totalScore), level: lastEval.level };
};

const levelWeights = { Básico: 1.0, Intermediário: 1.2, Avançado: 1.5 };

const Ranking = () => {
  const [fullRanking, setFullRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    const processRanking = async () => {
      setLoading(true);
      try {
        const studentsResponse = await requestGetStudents();
        const students = studentsResponse.data;
        const historyPromises = students.map((student) =>
          requestGetEvaluationHistory(student.id)
        );
        const historiesResponses = await Promise.all(historyPromises);
        const histories = historiesResponses.map((res) => res.data);

        const rankedStudents = students.map((student, index) => {
          const { score: baseScore_q, level: level_q } = calculateRankingScore(
            histories[index] || [],
            "Alongamento do Quadril"
          );

          const { score: baseScore_t, level: level_t } = calculateRankingScore(
            histories[index] || [],
            "Flexão do Tronco"
          );

          const currentLevel = level_q || level_t || "Básico";
          const weight = levelWeights[currentLevel];
          const finalScore = Math.round(
            ((baseScore_q + baseScore_t) / 2) * weight
          );
          return { name: student.name, level: currentLevel, score: finalScore };
        });

        const sorted = rankedStudents
          .sort((a, b) => b.score - a.score)
          .map((student, index) => ({ ...student, rank: index + 1 }));
        setFullRanking(sorted);
      } catch (error) {
        console.error("Erro ao processar o ranking:", error);
      } finally {
        setLoading(false);
      }
    };
    processRanking();
  }, []);

  const topThree = fullRanking.slice(0, 3);
  const others = fullRanking.slice(3, 10);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = others.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(others.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="pilates-ranking-container">
      <h2>
        <FaTrophy /> Top 10 Pilateiros
      </h2>
      <p className="pilates-ranking-subtitle">
        Score ponderado pelo nível do aluno
      </p>

      {loading ? (
        <div className="pilates-ranking-loading">Calculando ranking...</div>
      ) : fullRanking.length === 0 ? (
        <div className="pilates-ranking-no-data">
          Nenhum aluno no ranking ainda.
        </div>
      ) : (
        <>
          <div className="pilates-ranking-podium">
            {topThree[1] && (
              <div className="pilates-ranking-podium-item pilates-ranking-rank-2">
                <div className="pilates-ranking-podium-base">
                  <h2>2º</h2>
                  <p className="pilates-ranking-podium-name">
                    {topThree[1].name}
                  </p>
                  <span className="pilates-ranking-podium-score">
                    {topThree[1].score} pts
                  </span>
                </div>
              </div>
            )}
            {topThree[0] && (
              <div className="pilates-ranking-podium-item pilates-ranking-rank-1">
                <div className="pilates-ranking-podium-base">
                  <h2>1º</h2>
                  <p className="pilates-ranking-podium-name">
                    {topThree[0].name}
                  </p>
                  <span className="pilates-ranking-podium-score">
                    {topThree[0].score} pts
                  </span>
                </div>
              </div>
            )}
            {topThree[2] && (
              <div className="pilates-ranking-podium-item pilates-ranking-rank-3">
                <div className="pilates-ranking-podium-base">
                  <h2>3º</h2>
                  <p className="pilates-ranking-podium-name">
                    {topThree[2].name}
                  </p>
                  <span className="pilates-ranking-podium-score">
                    {topThree[2].score} pts
                  </span>
                </div>
              </div>
            )}
          </div>

          {currentItems.length > 0 && (
            <>
              <ul className="pilates-ranking-list">
                {currentItems.map((student) => (
                  <li key={student.rank} className="pilates-ranking-list-item">
                    <div className="pilates-ranking-list-rank">
                      {student.rank}º
                    </div>
                    <div className="pilates-ranking-list-name">
                      {student.name}
                    </div>
                    <div className="pilates-ranking-list-score">
                      {student.score} pts
                    </div>
                  </li>
                ))}
              </ul>
              {totalPages > 1 && (
                <div className="pilates-ranking-pagination">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (number) => (
                      <button
                        key={number}
                        onClick={() => paginate(number)}
                        className={
                          currentPage === number ? "pilates-ranking-active" : ""
                        }
                      >
                        {number}
                      </button>
                    )
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Ranking;
